/**
 * BPF Disclosure Controller — Phase 6.3.
 *
 * Generic, reusable open/disabled/reset lifecycle for a single
 * expandable "disclosure" element. Today: Product Details. Approved
 * for later, without redesigning this class: Picture Frame Details,
 * Accessory Details, Shipping Details, Care Instructions, or any
 * future Builder disclosure — see sections/bpf-memorial-designer.liquid's
 * own comment on the [data-disclosure="product-details"] element for
 * why only the data-disclosure value (and its own thin content-aware
 * wrapper, e.g. BpfProductDetailsDisclosure below) is specific to any
 * one disclosure; this class never is.
 *
 * Owns exactly three things: whether the disclosure is enabled,
 * whether it's open, and what its content slot currently holds.
 * Nothing else. It does not know what "the resolved product" is, does
 * not parse JSON, does not know Shopify or the CPD exist — the caller
 * decides WHEN to call setEnabled()/setContent()/reset() and WHY; this
 * class only ever reacts to those calls. That separation is what makes
 * it reusable for a future disclosure whose enabling condition has
 * nothing to do with a resolved product at all (e.g. a Shipping
 * Details disclosure that could reasonably always be enabled).
 *
 * Markup contract (see sections/bpf-memorial-designer.liquid for the
 * real, unstyled Phase 6.3 placeholder using it):
 *   <details data-disclosure="...">
 *     <summary data-disclosure-summary>...</summary>
 *     <div data-disclosure-content></div>
 *   </details>
 */

if (typeof window.BpfDisclosureController === 'undefined') {
  window.BpfDisclosureController = class BpfDisclosureController {
    constructor(detailsEl) {
      this.detailsEl = detailsEl || null;
      this.summaryEl = this.detailsEl ? this.detailsEl.querySelector('[data-disclosure-summary]') : null;
      this.contentEl = this.detailsEl ? this.detailsEl.querySelector('[data-disclosure-content]') : null;
      this._enabled = false;

      if (this.summaryEl) {
        // Same click-guard technique already proven for Review's own
        // lock (initReviewLock() in assets/bpf-memorial-designer.js) —
        // <details>'s native `toggle` event isn't cancelable, so a
        // disabled disclosure intercepts `click` on its own <summary>
        // instead, before the native open/close behavior applies. This
        // also blocks keyboard activation, since Enter/Space on a
        // focused <summary> dispatch a synthetic click first.
        this.summaryEl.addEventListener('click', (event) => {
          if (!this._enabled) event.preventDefault();
        });
      }
    }

    // Whether this disclosure may currently be opened — does not by
    // itself change open/closed state or content. Reflected onto the
    // markup as aria-disabled so a screen reader and any CSS state
    // styling both stay in sync with the one JS-owned source of truth.
    setEnabled(enabled) {
      this._enabled = Boolean(enabled);
      if (this.detailsEl) this.detailsEl.setAttribute('aria-disabled', this._enabled ? 'false' : 'true');
    }

    // Deliberately conservative by default: a plain string is always
    // treated as text (setEnabled()'s caller decides whether real
    // markup is safe to inject — see BpfProductDetailsDisclosure below
    // for the one place that currently does, and why). Passing a Node
    // (e.g. a DocumentFragment or element built by the caller) renders
    // as real markup; nothing else does.
    setContent(content) {
      if (!this.contentEl) return;
      this.contentEl.textContent = '';
      if (content instanceof Node) {
        this.contentEl.appendChild(content);
      } else if (typeof content === 'string' && content) {
        this.contentEl.textContent = content;
      }
    }

    // Closes, clears, and disables in one call — the complete "the
    // thing this disclosure was about just changed, for any reason"
    // response every disclosure needs, regardless of what it displays.
    reset() {
      if (this.detailsEl) this.detailsEl.open = false;
      this.setContent(null);
      this.setEnabled(false);
    }
  };
}

/**
 * BpfProductDetailsDisclosure — Phase 6.3.
 *
 * The ONLY code in this file aware that "description" exists. A thin
 * wrapper around one BpfDisclosureController: given the current
 * Resolved Shopify Product (or null), it always resets first — see
 * sync()'s own comment for why that happens even when moving from one
 * valid product straight to another, never just silently swapping
 * content in place — then re-enables and fills in the description only
 * if a real one exists on the new product.
 *
 * A future BpfCareInstructionsDisclosure, BpfShippingDetailsDisclosure,
 * etc. would each be an equally thin wrapper reading a different field
 * (or a different resolved-content source entirely) through the exact
 * same underlying BpfDisclosureController — never a change to that
 * class, never a change to this file's lifecycle logic.
 */
if (typeof window.BpfProductDetailsDisclosure === 'undefined') {
  window.BpfProductDetailsDisclosure = class BpfProductDetailsDisclosure {
    constructor(detailsEl) {
      this.controller =
        detailsEl && window.BpfDisclosureController ? new window.BpfDisclosureController(detailsEl) : null;
    }

    // Called by the Builder whenever the resolved product changes, for
    // ANY reason (Companion, Breed, Memorial Style, or any future
    // eligibility logic) — see assets/bpf-memorial-designer.js's
    // syncProductDependentDisclosures(), which is the one place that
    // decides WHEN this runs; this method never inspects why it was
    // called, only what it now receives.
    //
    // shopifyProduct is either the full Resolved Shopify Product object
    // (from getResolvedShopifyProduct() — id, handle, title,
    // description, vendor, type, featuredImage, priceMin/priceMax/
    // priceVaries) or null. Always reset()s first — even when moving
    // from one valid product straight to a different valid one — per
    // the approved lifecycle: "close, clear, return to disabled, wait
    // for the new resolved product," not "swap content while staying
    // open."
    sync(shopifyProduct) {
      if (!this.controller) return;
      this.controller.reset();
      if (!shopifyProduct || !shopifyProduct.description) return;

      // Shopify's own trusted, merchant-authored rich text (Product
      // Description, edited in Shopify Admin) — the same trust model
      // this theme's own native product-description rendering already
      // uses elsewhere (e.g. snippets/main-product-tabs.liquid). Never
      // customer/user-supplied input, so building real markup from it
      // here is safe in a way BpfDisclosureController.setContent()'s
      // plain-string default deliberately isn't for arbitrary future
      // content sources.
      const wrapper = document.createElement('div');
      wrapper.innerHTML = shopifyProduct.description;
      this.controller.setContent(wrapper);
      this.controller.setEnabled(true);
    }
  };
}
